import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

import { To, UNSAFE_NavigationContext, useNavigate } from 'react-router-dom';
import type { Location, NavigateOptions, Navigator } from 'react-router-dom';

type BlockPrompt = boolean | string | ((location: Location) => unknown);
type BlockCleanup = () => void;
type BlockEntry = {
  prompt: BlockPrompt;
  token: symbol;
};

type AppNavigator = Navigator & {
  block?: (prompt?: BlockPrompt) => BlockCleanup;
};

function buildBlockedLocation(to: To): Location {
  if (typeof to === 'string') {
    const url = new URL(to, window.location.href);
    return {
      hash: url.hash,
      key: '',
      pathname: url.pathname,
      search: url.search,
      state: undefined,
    };
  }

  return {
    hash: to.hash ?? '',
    key: '',
    pathname: to.pathname ?? window.location.pathname,
    search: to.search ?? '',
    state: undefined,
  };
}

function blocksNavigation(prompt: BlockPrompt | undefined, location: Location): boolean {
  if (prompt === false || prompt === undefined) {
    return false;
  }

  if (prompt === true) {
    return true;
  }

  if (typeof prompt === 'string') {
    return !window.confirm(prompt);
  }

  const result = prompt(location);
  if (typeof result === 'string') {
    return !window.confirm(result);
  }

  return result === false;
}

export function AppNavigationProvider({ children }: PropsWithChildren) {
  const navigation = useContext(UNSAFE_NavigationContext);
  const promptsRef = useRef<BlockEntry[]>([]);

  const block = useCallback((prompt: BlockPrompt = true) => {
    const token = Symbol('navigation-blocker');
    promptsRef.current = promptsRef.current.concat({ prompt, token });

    return () => {
      promptsRef.current = promptsRef.current.filter((registeredPrompt) => registeredPrompt.token !== token);
    };
  }, []);

  const shouldBlockNavigation = useCallback((to: To) => {
    const location = buildBlockedLocation(to);

    return promptsRef.current.some(({ prompt }) => blocksNavigation(prompt, location));
  }, []);

  const navigator = useMemo<AppNavigator>(() => {
    const baseNavigator = navigation.navigator;

    return {
      ...baseNavigator,
      block,
      push(to: To, state?: unknown, opts?: NavigateOptions) {
        if (!shouldBlockNavigation(to)) {
          baseNavigator.push(to, state, opts);
        }
      },
      replace(to: To, state?: unknown, opts?: NavigateOptions) {
        if (!shouldBlockNavigation(to)) {
          baseNavigator.replace(to, state, opts);
        }
      },
    };
  }, [block, navigation.navigator, shouldBlockNavigation]);

  const value = useMemo(
    () => ({
      ...navigation,
      navigator,
    }),
    [navigation, navigator],
  );

  return React.createElement(UNSAFE_NavigationContext.Provider, { value }, children);
}

export default function useAppNavigation() {
  const navigate = useNavigate();
  const navigation = useContext(UNSAFE_NavigationContext);
  const navigator = navigation?.navigator as AppNavigator | undefined;

  const push = useCallback(
    (to: To, state?: unknown) => {
      navigate(to, { state });
    },
    [navigate],
  );

  const replace = useCallback(
    (to: To, state?: unknown) => {
      navigate(to, { replace: true, state });
    },
    [navigate],
  );

  const block = useCallback(
    (prompt?: BlockPrompt) => {
      return navigator?.block?.(prompt) ?? (() => undefined);
    },
    [navigator],
  );

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return { block, goBack, push, replace };
}
